'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { ArrowUp, Lock, Plus, X } from 'lucide-react';
import { useMountEffect } from '@/hooks/useMountEffect';
import { readClosetAuthToken } from '@/lib/closet/client-auth';
import { makeViewport, spreadRect } from '@/lib/bricks/stage/reading';
import type { GenerateJob, GenerateStage } from '@/lib/bricks/generator/job';
import { PROMPT_MAX } from '@/lib/bricks/generator/job';
import type { BrickSet, ShelfSet } from '@/lib/bricks/sets-shared';
import type { Generated, Unlock } from '@/lib/bricks/generate-set';
import type { SpreadRect } from './Reader';
import '@/app/bricks/generator.css';
const Reader = dynamic(() => import('./Reader'), {
  ssr: false
});

// The "type a product, get a set" box, folded into a single button until
// asked for. A real run (image model → 3D model → voxels → parts and steps,
// about a minute) takes the closet admin password; without it the box
// offers the closest set already on the shelf. A finished set opens in the
// same booklet reader as the curated ones, over the table.

const STAGES: {
  stage: GenerateStage;
  label: string;
}[] = [{
  stage: 'imagining',
  label: 'Imagining it'
}, {
  stage: 'sculpting',
  label: 'Sculpting it in 3D'
}, {
  stage: 'bricking',
  label: 'Cutting it into bricks'
}, {
  stage: 'done',
  label: 'Printing the booklet'
}];

// The seam reads every set's parts and copy, so it loads once the box is
// opened rather than with the page. Each helper resolves, never rejects.
const loadSeam = () => import('@/lib/bricks/generate-set').catch(() => null);
async function closest(text: string) {
  const seam = await loadSeam();
  if (!seam) return null;
  const {
    set,
    exact
  } = seam.matchSet(text);
  return {
    slug: set.slug,
    name: set.name,
    exact
  };
}
async function unlock(password: string): Promise<Unlock> {
  const seam = await loadSeam();
  return seam ? seam.unlockGenerator(password) : 'offline';
}
async function generate(text: string, onJob: (job: GenerateJob) => void): Promise<Generated> {
  const seam = await loadSeam();
  if (!seam) return {
    kind: 'error',
    message: 'Couldn’t load the generator.'
  };
  return seam.generateSet(text, onJob);
}
function measureSpread(): SpreadRect {
  const r = spreadRect(makeViewport(window.innerWidth, window.innerHeight));
  return {
    left: r.left,
    top: r.top,
    width: r.width,
    height: r.height,
    single: r.single
  };
}
const noop = () => {};

// A generated set's booklet, laid open over the table (the stage only
// knows the curated sets, so there's no cover to fly in from the shelf).
function GeneratedBooklet(t0) {
  const $ = _c(14);
  const {
    set,
    sets,
    onClose
  } = t0;
  const [rect, setRect] = useState(measureSpread);
  let t1;
  if ($[0] !== onClose) {
    t1 = () => {
      const onResize = () => setRect(measureSpread());
      const onKey = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      window.addEventListener("resize", onResize);
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("resize", onResize);
        window.removeEventListener("keydown", onKey);
      };
    };
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useMountEffect(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <X size={16} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClose) {
    t3 = <button type="button" className="bricks-generated-close" aria-label="Close the booklet" aria-keyshortcuts="Escape" title="Close the booklet (Esc)" onClick={onClose}>{t2}</button>;
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = String(rect.single);
  let t5;
  if ($[5] !== onClose || $[6] !== rect || $[7] !== set || $[8] !== sets || $[9] !== t4) {
    t5 = <Reader key={t4} slug={set.slug} generated={set} allSets={sets} rect={rect} shown="open" closing={false} resume={null} onInside={noop} onPage={noop} onClose={onClose} onBare={noop} />;
    $[5] = onClose;
    $[6] = rect;
    $[7] = set;
    $[8] = sets;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <div className="bricks-generated">{t3}{t5}</div>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function Progress(t0) {
  const $ = _c(14);
  const {
    stage,
    image
  } = t0;
  let t1;
  if ($[0] !== stage) {
    t1 = STAGES.findIndex(s => s.stage === stage);
    $[0] = stage;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const at = Math.max(0, t1);
  let t2;
  if ($[2] !== image) {
    t2 = image ? <Image className="bricks-generator-thumb" src={image} alt="" width={28} height={28} unoptimized={true} /> : <div className="bricks-generator-grid" aria-hidden="true">{Array.from({
        length: 24
      }, _temp)}</div>;
    $[2] = image;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = STAGES[at];
  let t4;
  if ($[4] !== t3.label) {
    t4 = <span>{t3.label}…</span>;
    $[4] = t3.label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== at) {
    t5 = STAGES.slice(0, 3).map((s_0, i_0) => <i key={s_0.stage} data-done={i_0 < at || undefined} data-now={i_0 === at || undefined} />);
    $[6] = at;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="bricks-generator-steps" aria-hidden="true">{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t4 || $[12] !== t6) {
    t7 = <div className="bricks-generator-progress" role="status">{t2}{t4}{t6}</div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function _temp(_, i) {
  return <span key={i} style={{
    animationDelay: `${i * 90}ms`
  }} />;
}
export function Generator(t0) {
  const $ = _c(43);
  const {
    sets,
    onReveal
  } = t0;
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [password, setPassword] = useState("");
  const [asking, setAsking] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [stage, setStage] = useState(null);
  const [image, setImage] = useState(null);
  const [note, setNote] = useState(null);
  const [made, setMade] = useState(null);
  const busy = stage !== null || unlocking;
  let t1;
  if ($[0] !== busy) {
    t1 = text => {
      if (busy || !text.trim()) {
        return;
      }
      setNote(null);
      if (!readClosetAuthToken()) {
        setAsking(true);
        return;
      }
      setAsking(false);
      setImage(null);
      setStage("imagining");
      generate(text, job => {
        setStage(job.stage === "failed" ? "bricking" : job.stage);
        if (job.image) {
          setImage(job.image);
        }
      }).then(result => {
        setStage(null);
        if (result.kind === "done") {
          setPrompt("");
          setOpen(false);
          setMade(result.set);
        }
        if (result.kind === "locked") {
          setAsking(true);
          setNote("That password no longer works.");
        }
        if (result.kind === "error") {
          setNote(result.message);
        }
      });
    };
    $[0] = busy;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const start = t1;
  let t2;
  if ($[2] !== busy || $[3] !== password || $[4] !== prompt || $[5] !== start) {
    t2 = () => {
      if (busy || !password) {
        return;
      }
      setUnlocking(true);
      setNote(null);
      unlock(password).then(result_0 => {
        setUnlocking(false);
        setPassword("");
        if (result_0 === "ok") {
          start(prompt);
        }
        if (result_0 === "wrong") {
          setNote("Wrong password.");
        }
        if (result_0 === "offline") {
          setNote("Couldn\u2019t reach the server.");
        }
      });
    };
    $[2] = busy;
    $[3] = password;
    $[4] = prompt;
    $[5] = start;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const submitPassword = t2;
  let t3;
  if ($[7] !== onReveal || $[8] !== prompt) {
    t3 = () => {
      setAsking(false);
      closest(prompt).then(set => {
        if (!set) {
          return;
        }
        setPrompt("");
        setOpen(false);
        if (!set.exact) {
          setNote(`Closest on the shelf: ${set.name}`);
        }
        onReveal(set.slug);
      });
    };
    $[7] = onReveal;
    $[8] = prompt;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const showClosest = t3;
  if (made) {
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = () => setMade(null);
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== made || $[12] !== sets) {
      t5 = <GeneratedBooklet set={made} sets={sets} onClose={t4} />;
      $[11] = made;
      $[12] = sets;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (!open && stage === null) {
    let t4;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <button type="button" className="bricks-generator-toggle" aria-label="Turn any product into a set" title="Turn any product into a set" onClick={() => {
        setNote(null);
        setOpen(true);
        loadSeam();
      }}><Plus size={18} /></button>;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== note) {
      t5 = note && <span className="bricks-generator-note" role="status">{note}</span>;
      $[15] = note;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== t5) {
      t6 = <div className="bricks-generator">{t4}{t5}</div>;
      $[17] = t5;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    return t6;
  }
  let t4;
  if ($[19] !== asking || $[20] !== busy || $[21] !== password || $[22] !== prompt || $[23] !== start || $[24] !== submitPassword || $[25] !== unlocking) {
    t4 = asking ? <form onSubmit={e => {
      e.preventDefault();
      submitPassword();
    }} onKeyDown={e_0 => {
      if (e_0.key === "Escape" && !busy) {
        setAsking(false);
      }
    }}><Lock size={14} className="bricks-generator-lock" /><input type="password" value={password} onChange={e_1 => setPassword(e_1.target.value)} placeholder="Password to generate" aria-label="Password to generate a set" autoComplete="current-password" disabled={unlocking} ref={_temp2} /><button type="submit" aria-label="Unlock and generate" disabled={unlocking || !password}><ArrowUp size={16} /></button></form> : <form onSubmit={e_2 => {
      e_2.preventDefault();
      start(prompt);
    }} onKeyDown={e_3 => {
      if (e_3.key === "Escape" && !busy) {
        setOpen(false);
      }
    }}><input value={prompt} onChange={e_4 => setPrompt(e_4.target.value)} onBlur={() => {
        if (!prompt.trim() && !busy) {
          setOpen(false);
        }
      }} placeholder="Any product" aria-label="Product to turn into a brick set" disabled={busy} maxLength={PROMPT_MAX} ref={_temp3} /><button type="submit" aria-label="Generate set" disabled={busy || !prompt.trim()}><ArrowUp size={16} /></button></form>;
    $[19] = asking;
    $[20] = busy;
    $[21] = password;
    $[22] = prompt;
    $[23] = start;
    $[24] = submitPassword;
    $[25] = unlocking;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  let t5;
  if ($[27] !== image || $[28] !== stage) {
    t5 = stage !== null && <Progress stage={stage} image={image} />;
    $[27] = image;
    $[28] = stage;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== asking || $[31] !== showClosest || $[32] !== unlocking) {
    t6 = asking && !unlocking && <span className="bricks-generator-note">Each set costs a few cents to make.{" "}<button type="button" onClick={showClosest}>Show the closest set instead</button></span>;
    $[30] = asking;
    $[31] = showClosest;
    $[32] = unlocking;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] !== note || $[35] !== stage) {
    t7 = note && stage === null && <span className="bricks-generator-note" role="status">{note}</span>;
    $[34] = note;
    $[35] = stage;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== busy || $[38] !== t4 || $[39] !== t5 || $[40] !== t6 || $[41] !== t7) {
    t8 = <div className="bricks-generator" data-busy={busy}>{t4}{t5}{t6}{t7}</div>;
    $[37] = busy;
    $[38] = t4;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  return t8;
}
function _temp3(node_0) {
  return node_0?.focus();
}
function _temp2(node) {
  return node?.focus();
}